/* For documentation for these models, see models.py on the backend. */

import { makePlainObject } from "../utils";

var nextKey = 0;
export type UUID = string;
export type ModelClass<T extends ErnotsAbstractModel = ErnotsAbstractModel> = new () => T;

/**
 * Creates a copy of the input object, or converts the input class instance to a plain object.
 * (Redux stores don't allow instances.)
 * 
 * Also assigns a key for indexing.
 */
export function copyRestObject<T extends ErnotsAbstractModel>(obj: T, modelClass: ModelClass<T>)
{
    const ret = makePlainObject(obj);

    ret.modelClassName = classNameMap[modelClass.name] || '';

    if (!ret.key)
    {
        ret.key = ret.id || `${++nextKey}`;
    }

    return ret as T;
}

export abstract class ErnotsAbstractModel
{
    id = '';
    created = '';
    modified = '';
    public = false;

    /**
     * The name of the class that created this instance. If the instance was converted to a plain object, it would
     * lose information about the creating class, unless I record it elsewhere.
     */
    modelClassName = '';

    /** 
     * The key is either the object's database ID (a UUID), or if there isn't one yet, a serially assigned integer,
     * so objects can always be indexed.
     * (Objects fetched from the server also have a key set to ID on the server side, for consistency.)
     */
    key = '';
}

export class Language extends ErnotsAbstractModel
{
    name = '';
    gtrans_code = '';
}

abstract class BookBase extends ErnotsAbstractModel
{
    title = '';
    source_text = '';
    is_html = false;
    source_language = '' as UUID;
    bookmark = 0;
    manually_edited = false;
    unsaved_changes = false;
}

export class Book extends BookBase
{
    htmlblock_set = [] as HTMLBlock[];
    booklanguage_set = [] as BookLanguage[];
    sentence_set = [] as Sentence[];
    abbreviations = '';
    non_text_elements = 'img, audio, video, canvas, svg, map, progress, meter, code';
    skipped_elements = 'input, textarea, select, iframe';
    page_size = 100;
}

export class BookForList extends BookBase
{
    total_sentences = 0;
}

export class BookLanguagePreset extends ErnotsAbstractModel
{
    source_language = '' as UUID;
    presetlanguage_set = [] as PresetLanguage[];
}

export class BookLanguage extends ErnotsAbstractModel
{
    book = '' as UUID;
    language = '' as UUID;
    translation = '';
    display_order = Number.MAX_SAFE_INTEGER;
}

export class PresetLanguage extends ErnotsAbstractModel
{
    language = '' as UUID;
    display_order = Number.NEGATIVE_INFINITY;
}

export class Abbreviation extends ErnotsAbstractModel
{
    language = '' as UUID;
    text = ''; //Note to self: max length 100.
}

export class HTMLBlock extends ErnotsAbstractModel
{
    //See Python model file.
    //c = container; i = inline; n = non-text, p = plain-text paragraph
    type = 'c' as 'c' | 'i' | 'n' | 'p';

    position = 0;
    block_id = 0;
    parent_block_id = -1;

    html = null as string | null;
}

export class Sentence extends ErnotsAbstractModel
{
    //See Python model file.
    //p = plain text; a = alt; t = title; n = non-text;
    type = 'p' as 'p' | 'a' | 't' | 'n';

    parent_block_id = 0;
    position = 0;
    sentence_id = 0;
    text = null as string | null;
    html = null as string | null;
}

export class DictionaryLink extends ErnotsAbstractModel
{
    text = '';
    urlpattern = '';
    language = '' as UUID | null;
}

//Workaround for Webpack adding "src_" to the names of all my classes.
//I need to know their actual names - and the names of the fields - to communicate with the backend.
export const classNameMap = Object.fromEntries([
    [Language.name, 'Language'],
    [Book.name, 'Book'],
    [BookForList.name, 'BookForList'],
    [BookLanguage.name, 'BookLanguage'],
    [BookLanguagePreset.name, 'BookLanguagePreset'],
    [PresetLanguage.name, 'PresetLanguage'],
    [BookLanguage.name, 'BookLanguage'],
    [Abbreviation.name, 'Abbreviation'],
    [HTMLBlock.name, 'HTMLBlock'],
    [Sentence.name, 'Sentence'],
    [DictionaryLink.name, 'DictionaryLink'],
])
